// =============================================================
// knob.js — Rotary knob controller for navigation.
// Replaces standard dots with a tactile, 6-state rotary dial
// and a Web Audio "tick" sound.
// =============================================================

const isReducedMotion = () =>
  window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

let actx = null;
function playTick() {
  if (!actx) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    actx = new AudioContext();
  }
  if (actx.state === "suspended") actx.resume();

  const osc = actx.createOscillator();
  const gain = actx.createGain();

  // "Mechanical tick" sound
  osc.type = "square";
  osc.frequency.setValueAtTime(600, actx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(100, actx.currentTime + 0.02);

  gain.gain.setValueAtTime(0.3, actx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, actx.currentTime + 0.02);

  osc.connect(gain);
  gain.connect(actx.destination);

  osc.start();
  osc.stop(actx.currentTime + 0.02);
}

export class NavKnob {
  constructor(el, slider) {
    this.el = el;
    this.slider = slider;
    this.total = slider.total;
    
    // Angle settings (like a guitar amp knob: -120deg to 120deg)
    this.minAngle = -135;
    this.maxAngle = 135;
    this.range = this.maxAngle - this.minAngle;
    this.stepAngle = this.range / (this.total - 1);
    
    this.currentStep = slider.current;
    this.isDragging = false;
    this.center = { x: 0, y: 0 };

    this._buildUI();
    this._bind();
    this.setStep(this.currentStep, true);
  }

  _buildUI() {
    this.el.innerHTML = `
      <div class="knob-ring"></div>
      <div class="knob-dial">
        <div class="knob-pointer"></div>
      </div>
    `;
    this.dial = this.el.querySelector(".knob-dial");
    this.ring = this.el.querySelector(".knob-ring");

    // Create dot markers on the ring
    for (let i = 0; i < this.total; i++) {
      const angle = this.minAngle + (i * this.stepAngle);
      const dotWrap = document.createElement("div");
      dotWrap.className = "knob-marker";
      dotWrap.style.transform = `rotate(${angle}deg)`;
      
      const dotInner = document.createElement("div");
      dotInner.className = "knob-marker-inner";
      dotWrap.appendChild(dotInner);
      
      this.ring.appendChild(dotWrap);
    }
    this.markers = [...this.ring.querySelectorAll(".knob-marker")];
  }

  _bind() {
    this.el.addEventListener("pointerdown", this._onPointerDown.bind(this));
    window.addEventListener("pointermove", this._onPointerMove.bind(this));
    window.addEventListener("pointerup", this._onPointerUp.bind(this));
    window.addEventListener("pointercancel", this._onPointerUp.bind(this));

    // Optional keyboard control
    this.el.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        this.slider.next();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        this.slider.prev();
      }
    });

    // Listen to slider changes from outside (e.g. wheel, arrow buttons)
    const origOnChange = this.slider.onChange;
    this.slider.onChange = (idx, info) => {
      this.setStep(idx, false);
      if (origOnChange) origOnChange(idx, info);
    };
  }

  _updateCenter() {
    const rect = this.dial.getBoundingClientRect();
    this.center = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  }

  _onPointerDown(e) {
    this.isDragging = true;
    this.el.classList.add("is-dragging");
    this.el.setPointerCapture(e.pointerId);
    this._updateCenter();
    this._handlePointer(e);
  }

  _onPointerMove(e) {
    if (!this.isDragging) return;
    this._handlePointer(e);
  }

  _onPointerUp(e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.el.classList.remove("is-dragging");
    this.el.releasePointerCapture(e.pointerId);
    // Snap to nearest step completely
    this.setStep(this.currentStep, false);
  }

  _handlePointer(e) {
    // Calculate angle
    const dx = e.clientX - this.center.x;
    const dy = e.clientY - this.center.y;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90; // +90 so top is 0
    
    if (angle < -180) angle += 360;
    if (angle > 180) angle -= 360;

    // Constrain angle
    if (angle < this.minAngle - 20 && angle > -180) angle = this.minAngle;
    if (angle > this.maxAngle + 20 && angle <= 180) angle = this.maxAngle;
    
    // Map angle to step
    let fraction = (angle - this.minAngle) / this.range;
    fraction = Math.max(0, Math.min(1, fraction));
    
    let step = Math.round(fraction * (this.total - 1));
    
    // Animate rotation strictly with mouse if dragging
    if (window.gsap && !isReducedMotion()) {
      window.gsap.to(this.dial, { rotation: angle, duration: 0.1, ease: "none", overwrite: true });
    } else {
      this.dial.style.transform = `rotate(${angle}deg)`;
    }

    if (step !== this.currentStep) {
      this.currentStep = step;
      playTick();
      this._updateMarkers();
      this.el.setAttribute("aria-valuenow", step);
      this.slider.goTo(step);
    }
  }

  setStep(step, instant = false) {
    if (this.isDragging) return; // let dragging handle itself
    if (step !== this.currentStep && !instant) playTick();
    this.currentStep = step;
    
    const angle = this.minAngle + (step * this.stepAngle);
    if (window.gsap && !isReducedMotion()) {
      if (instant) {
        window.gsap.set(this.dial, { rotation: angle });
      } else {
        window.gsap.to(this.dial, { rotation: angle, duration: 0.4, ease: "back.out(1.4)" });
      }
    } else {
      this.dial.style.transform = `rotate(${angle}deg)`;
    }
    
    this._updateMarkers();
    this.el.setAttribute("aria-valuenow", step);
  }

  _updateMarkers() {
    this.markers.forEach((m, i) => {
      m.classList.toggle("is-active", i === this.currentStep);
    });
  }
}
